import { GtmEventNames, type GtmEvent, type GtmVariable } from './types';
import { trackStartEvent } from './event-trigger';
import { trackScroll } from './event-tracking';
import { env } from '$env/dynamic/public';
import { browser } from '$app/environment';

const GTM_TAG_MANAGER_ID = env.PUBLIC_GTM_TAG_MANAGER_ID;

export function getGtmJsUrl() {
  if (!GTM_TAG_MANAGER_ID) {
    return undefined;
  }
  return `https://www.googletagmanager.com/gtm.js?id=${GTM_TAG_MANAGER_ID}&l=dataLayer`;
}

export const shouldLoadGtm = (currentUrl: string) => {
  return !!GTM_TAG_MANAGER_ID && new URL(currentUrl).searchParams.get('gtm') !== 'off';
};

export function gtmInit(currentUrl: string) {
  if (!shouldLoadGtm(currentUrl)) {
    return;
  }
  initEvents();
  renderGtmScript();
  renderGtmNoScript();
  trackScroll();
  startListeningToCustomGtmEvents();
}

function renderGtmScript() {
  const headElement = document.head;
  const gtmScriptTag = document.createElement('script');
  gtmScriptTag.async = true;
  const nonceElement = document.querySelector('[nonce]');
  gtmScriptTag.src = getGtmJsUrl()!;
  const nonce =
    (nonceElement as unknown as { nonce: string | undefined })?.nonce ||
    nonceElement?.getAttribute('nonce');
  if (nonce) {
    gtmScriptTag.setAttribute('nonce', nonce);
  }
  headElement.insertBefore(gtmScriptTag, headElement.firstChild);
}

function renderGtmNoScript() {
  const noScriptTag = document.createElement('noscript');
  noScriptTag.innerHTML = `<iframe
      src="https://www.googletagmanager.com/ns.html?id=${GTM_TAG_MANAGER_ID}"
      height="0"
      width="0"
      style="display:none;visibility:hidden"
    ></iframe>`;
  document.body.insertBefore(noScriptTag, document.body.firstChild);
}

function initEvents() {
  // Check if any event is of type GtmEventNames.Start
  const hasGtmStart = window.dataLayer?.some(
    (gtmEvent) => (gtmEvent as GtmEvent)?.event === GtmEventNames.Start,
  );
  // If there's no GTM start event, track the start event
  if (!hasGtmStart) {
    trackStartEvent();
  }
}

export function pushEvent<T extends GtmEvent>(event: T) {
  if (browser) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
}

export function pushVariable<T extends GtmVariable>(variable: T) {
  if (browser) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(variable);
  }
}

function startListeningToCustomGtmEvents() {
  document.addEventListener('gtm-event', (event) => {
    const gtmEvent = (event as CustomEvent).detail;
    pushEvent(gtmEvent);
  });
}
